import React, { useEffect, useState, useRef } from 'react';
import api from '../api/axiosConfig';
import ReactPlayer from 'react-player';
import { useTranslation } from 'react-i18next';
import { getActionLabel } from '../cantera/utils/actionUtils';
import { formatTime, formatGameDate, getSportSeason, getCurrentSeason } from '../cantera/utils/timeUtils';
import { computeAdvancedStats, computeSeasonAverages } from '../cantera/utils/statsCalculator';
import DrawingCanvas from '../cantera/components/DrawingCanvas';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

export default function CanteraStatsTab({ playerId }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allActions, setAllActions] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);

  // Separate state for grouped tags
  const [groupedTags, setGroupedTags] = useState([]);

  const [selectedActionType, setSelectedActionType] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Dimensiones dinámicas del reproductor y del canvas
  const [playerSize, setPlayerSize] = useState({ width: 640, height: 360 });
  const playerWrapperRef = useRef(null);
  const playerRef = useRef(null);

  // Herramientas de dibujo
  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [drawTool, setDrawTool] = useState('pen'); // 'pen' o 'eraser'
  const [drawColor, setDrawColor] = useState('#ff0000'); // Color por defecto

  // Encadenamiento automático
  const [autoChainActions, setAutoChainActions] = useState(true);
  const [currentActionStart, setCurrentActionStart] = useState(null);

  // 1) Cargar la lista de partidos para este jugador
  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    api
      .get(`/api/stats/player-stats/${playerId}`)
      .then((res) => {
        const gamesData = res.data || [];
        setGames(gamesData);

        // Establecer la temporada actual por defecto
        if (gamesData.length > 0 && !selectedSeason) {
          const currentSeason = getCurrentSeason();
          setSelectedSeason(currentSeason);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al obtener los partidos:', err);
        setError(t('canteraStats.errorLoading'));
        setLoading(false);
      });
  }, [playerId]);

  // 2) Cuando se selecciona un partido, cargar sus acciones
  useEffect(() => {
    if (!selectedGameId) {
      setSelectedGame(null);
      setAllActions([]);
      setFilteredActions([]);
      return;
    }

    const match = games.find((g) => g.game.id === parseInt(selectedGameId, 10));
    setSelectedGame(match || null);

    if (!match) {
      setAllActions([]);
      setFilteredActions([]);
      return;
    }

    api
      .get(`/api/stats/actions/${match.game.id}`)
      .then((res) => {
        const data = res.data || {};
        const actions = data.actions || [];

        // Calcular "realTimeSec" para cada acción
        const {
          periode1 = 0,
          periode2 = 0,
          periode3 = 0,
          periode4 = 0
        } = match.game;

        const periodOffsets = [
          periode1 || 0,
          periode2 || 0,
          periode3 || 0,
          periode4 || 0
        ];

        const actionsWithRealTime = actions
          .filter(act => act.player_id === parseInt(playerId, 10)) // Filtrer uniquement les actions du joueur
          .map((act) => {
            if (act.videoTimestamp !== undefined && act.videoTimestamp !== null) {
              return {
                ...act,
                realTimeSec: act.videoTimestamp
              };
            } else {
              const periodIndex = (act.period || 1) - 1;
              const offset = periodOffsets[periodIndex] || 0;
              const calculatedTime = act.timeSec + offset;
              return {
                ...act,
                realTimeSec: calculatedTime
              };
            }
          });

        // Separate tags from regular actions
        const regularActions = [];
        const tagsByNameMap = new Map(); // Group all tags by name only

        actionsWithRealTime.forEach(act => {
          if (act.action && act.action.startsWith('Tag:')) {
            // Extract tag name
            const tagName = act.action.substring(4).trim();

            if (!tagsByNameMap.has(tagName)) {
              tagsByNameMap.set(tagName, {
                tagName,
                instances: [] // Array of instances with timestamps
              });
            }

            // Add instance with this timestamp
            const tagData = tagsByNameMap.get(tagName);
            tagData.instances.push({
              realTimeSec: act.realTimeSec,
              period: act.period,
              timeSec: act.timeSec
            });
          } else {
            regularActions.push(act);
          }
        });

        // Convert grouped tags to array with consolidated counts
        const groupedTagsArray = Array.from(tagsByNameMap.values()).map(tagData => {
          return {
            tagName: tagData.tagName,
            action: `Tag: ${tagData.tagName}`,
            instances: tagData.instances,
            instanceCount: tagData.instances.length,
            isGroupedTag: true
          };
        });

        setGroupedTags(groupedTagsArray);
        setAllActions(regularActions);
        setFilteredActions(regularActions);
      })
      .catch((err) => {
        console.error('Error al obtener las acciones:', err);
      });
  }, [selectedGameId, games, playerId]);

  // Cuando cambie el tamaño del contenedor del reproductor, ajustamos el canvas
  useEffect(() => {
    if (!playerWrapperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setPlayerSize({ width, height });
      }
    });

    observer.observe(playerWrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const getTimesForActionType = (actionType) => {
    // Check if it's a grouped tag
    if (actionType && actionType.startsWith('Tag:')) {
      const matchingTag = groupedTags.find((t) => t.action === actionType);
      if (matchingTag) {
        // Return all timestamps from all instances of this tag
        const times = [];
        matchingTag.instances.forEach(instance => {
          times.push(instance.realTimeSec);
        });
        return times.sort((a, b) => a - b);
      }
      return [];
    }

    // Regular actions
    return filteredActions
      .filter((a) => a.action === actionType)
      .map((a) => a.realTimeSec)
      .sort((a, b) => a - b);
  };

  const handleSelectActionType = (type) => {
    setSelectedActionType(type);
    setCurrentIndex(0);
    const times = getTimesForActionType(type);
    if (times.length > 0) {
      seekToTime(times[0]);
      setCurrentActionStart(times[0]);
      setPlaying(true);
    }
    // Fermer le drawer mobile après sélection
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handlePrev = () => {
    if (!selectedActionType) return;
    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;
    setCurrentIndex((prev) => {
      const newIndex = Math.max(0, prev - 1);
      seekToTime(times[newIndex]);
      setCurrentActionStart(times[newIndex]);
      return newIndex;
    });
  };

  const handleNext = () => {
    if (!selectedActionType) return;
    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;
    setCurrentIndex((prev) => {
      const newIndex = Math.min(times.length - 1, prev + 1);
      seekToTime(times[newIndex]);
      setCurrentActionStart(times[newIndex]);
      return newIndex;
    });
  };

  const seekToTime = (sec) => {
    if (playerRef.current) {
      playerRef.current.seekTo(sec, 'seconds');
      setPlaying(true);
    }
  };

  const skipTime = (delta) => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + delta, 'seconds');
    }
  };

  const handlePlay = () => {
    setPlaying(true);
  };
  const handlePause = () => {
    setPlaying(false);
  };
  const handleSlow = () => {
    setPlaybackRate(0.5);
  };
  const handleNormalSpeed = () => {
    setPlaybackRate(1);
  };

  const handleClearDrawing = () => {
    const canvas = playerWrapperRef.current?.querySelector('canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleProgress = (progressState) => {
    if (!autoChainActions) return;
    if (!selectedActionType) return;
    if (!playing) return;

    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;

    if (
      currentActionStart !== null &&
      progressState.playedSeconds >= currentActionStart + 10
    ) {
      handleNext();
    }
  };

  // Agrupar partidos por temporada
  const groupGamesBySeason = () => {
    const grouped = {};
    games.forEach((g) => {
      const season = getSportSeason(g.game.game_date);
      if (!grouped[season]) {
        grouped[season] = [];
      }
      grouped[season].push(g);
    });

    Object.keys(grouped).forEach((season) => {
      grouped[season].sort((a, b) => {
        return new Date(b.game.game_date) - new Date(a.game.game_date);
      });
    });

    return grouped;
  };

  const getAvailableSeasons = () => {
    const seasons = new Set();
    games.forEach((g) => {
      seasons.add(getSportSeason(g.game.game_date));
    });
    return Array.from(seasons).sort((a, b) => {
      const [yearA] = a.split('-').map(Number);
      const [yearB] = b.split('-').map(Number);
      return yearB - yearA;
    });
  };

  const gamesBySeason = groupGamesBySeason();
  const availableSeasons = getAvailableSeasons();
  const filteredGames = selectedSeason ? (gamesBySeason[selectedSeason] || []) : games;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (games.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('canteraStats.noStats')}
        </Alert>
      </Box>
    );
  }

  // Contenu de la sidebar (utilisé pour desktop et mobile)
  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#f9f9f9'
      }}
    >
        {/* Section des filtres compacte */}
        <Box sx={{ p: 2, borderBottom: '1px solid #ddd', bgcolor: 'white' }}>
          {/* Selección de temporada */}
          {availableSeasons.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel id="select-season-label">{t('canteraStats.season')}</InputLabel>
              <Select
                labelId="select-season-label"
                label={t('canteraStats.season')}
                value={selectedSeason}
                onChange={(e) => {
                  setSelectedSeason(e.target.value);
                  setSelectedGameId('');
                }}
              >
                <MenuItem value="">-- {t('canteraStats.allSeasons')} --</MenuItem>
                {availableSeasons.map((season) => {
                  const gamesCount = gamesBySeason[season]?.length || 0;
                  return (
                    <MenuItem key={season} value={season}>
                      {season} ({gamesCount})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Selección del partido */}
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel id="select-match-label">{t('canteraStats.match')}</InputLabel>
            <Select
              labelId="select-match-label"
              label={t('canteraStats.match')}
              value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value)}
            >
              <MenuItem value="">-- {t('canteraStats.noMatch')} --</MenuItem>
              {filteredGames.map((g) => (
                <MenuItem key={g.game.id} value={g.game.id}>
                  {g.game.game_label} {t('canteraStats.vs')} {g.game.adversary_name} - {formatGameDate(g.game.game_date)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Estadísticas del jugador */}
          {selectedGame && selectedGame.playerStats && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('canteraStats.matchStats')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip label={`${t('canteraStats.minutes')}: ${selectedGame.playerStats.minutes_played}`} size="small" />
                <Chip label={`${t('canteraStats.points')}: ${selectedGame.playerStats.points}`} size="small" color="primary" />
                <Chip label={`${t('canteraStats.rebounds')}: ${selectedGame.playerStats.rebounds}`} size="small" color="success" />
                <Chip label={`${t('canteraStats.assists')}: ${selectedGame.playerStats.assists}`} size="small" color="info" />
                <Chip label={`${t('canteraStats.steals')}: ${selectedGame.playerStats.steals}`} size="small" />
                <Chip label={`${t('canteraStats.blocks')}: ${selectedGame.playerStats.blocks}`} size="small" color="secondary" />
                <Chip label={`${t('canteraStats.turnovers')}: ${selectedGame.playerStats.turnovers}`} size="small" color="warning" />
                <Chip label={`${t('canteraStats.fouls')}: ${selectedGame.playerStats.fouls}`} size="small" color="error" />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`${t('canteraStats.fieldGoals')}: ${selectedGame.playerStats.field_goals_made}/${selectedGame.playerStats.field_goals_attempted}`}
                  size="small"
                />
                <Chip
                  label={`${t('canteraStats.threePoints')}: ${selectedGame.playerStats.three_points_made}/${selectedGame.playerStats.three_points_attempted}`}
                  size="small"
                />
                <Chip
                  label={`${t('canteraStats.freeThrows')}: ${selectedGame.playerStats.free_throws_made}/${selectedGame.playerStats.free_throws_attempted}`}
                  size="small"
                />
              </Box>

              {/* Stats avancées calculées */}
              {(() => {
                const adv = computeAdvancedStats(selectedGame.playerStats);
                if (!adv || adv.ts_pct === null) return null;
                return (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      {t('canteraStats.advancedStats', { defaultValue: 'Stats avancées' })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {adv.ts_pct !== null && <Chip label={`TS%: ${adv.ts_pct}`} size="small" sx={{ bgcolor: '#1976d2', color: 'white' }} />}
                      {adv.efg_pct !== null && <Chip label={`eFG%: ${adv.efg_pct}`} size="small" sx={{ bgcolor: '#1565c0', color: 'white' }} />}
                      {adv.ast_to_ratio !== null && <Chip label={`AST/TO: ${adv.ast_to_ratio}`} size="small" sx={{ bgcolor: '#0d47a1', color: 'white' }} />}
                      {adv.pts_per_min !== null && <Chip label={`PTS/min: ${adv.pts_per_min}`} size="small" sx={{ bgcolor: '#283593', color: 'white' }} />}
                    </Box>
                    {adv.per_36 && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mt: 1, mb: 0.5 }}>
                          {t('canteraStats.per36', { defaultValue: 'Per 36 minutes' })}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={`PTS: ${adv.per_36.points}`} size="small" variant="outlined" />
                          <Chip label={`REB: ${adv.per_36.rebounds}`} size="small" variant="outlined" />
                          <Chip label={`AST: ${adv.per_36.assists}`} size="small" variant="outlined" />
                          <Chip label={`STL: ${adv.per_36.steals}`} size="small" variant="outlined" />
                          <Chip label={`BLK: ${adv.per_36.blocks}`} size="small" variant="outlined" />
                        </Box>
                      </>
                    )}
                  </>
                );
              })()}
            </Box>
          )}
        </Box>

        {/* Tags et Actions - scrollable */}
        {selectedGame && (filteredActions.length > 0 || groupedTags.length > 0) && (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Tags Section */}
            {groupedTags.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <LocalOfferIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('canteraStats.teamTags')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {groupedTags.map((tag, index) => {
                    const isSelected = selectedActionType === tag.action;
                    return (
                      <Card
                        key={`tag_${index}`}
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          bgcolor: isSelected ? 'primary.main' : 'background.paper',
                          color: isSelected ? 'white' : 'text.primary',
                          border: '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'primary.light',
                          '&:hover': {
                            boxShadow: 2,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => handleSelectActionType(tag.action)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {tag.tagName}
                          </Typography>
                          {tag.instanceCount > 1 && (
                            <Chip
                              label={`x${tag.instanceCount}`}
                              size="small"
                              color="secondary"
                              sx={{ height: '18px', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Regular Actions */}
            {filteredActions.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                  {t('canteraStats.individualActions')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  {Array.from(new Set(filteredActions.map((a) => a.action))).map((type) => {
                    const count = filteredActions.filter((a) => a.action === type).length;
                    const isSelected = selectedActionType === type;
                    const displayLabel = getActionLabel(type, t);
                    return (
                      <Button
                        key={type}
                        variant={isSelected ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleSelectActionType(type)}
                        sx={{
                          justifyContent: 'space-between',
                          textTransform: 'none',
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }}
                      >
                        <span>{displayLabel}</span>
                        <Chip label={count} size="small" sx={{ height: '18px', minWidth: '28px' }} />
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      // Optimisation pour mobile en mode paysage
      '@media (max-width: 900px) and (orientation: landscape)': {
        height: '100vh',
        '& video': {
          objectFit: 'contain'
        }
      }
    }}>
      {/* SIDEBAR DESKTOP - Masqué sur mobile */}
      {!isMobile && (
        <Box
          sx={{
            width: '380px',
            borderRight: '1px solid #ddd',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f9f9f9',
            overflowY: 'auto'
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* DRAWER MOBILE */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '85%',
              maxWidth: '360px',
              height: '100%',
              overflowY: 'auto'
            }
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderBottom: '1px solid #ddd',
            bgcolor: 'primary.main',
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {t('canteraStats.filtersAndActions')}
            </Typography>
            <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {sidebarContent}
        </Drawer>
      )}

      {/* ZONE PRINCIPALE - Video et contrôles */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Bouton menu mobile - toujours visible */}
        {isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1000,
              bgcolor: 'primary.main',
              borderRadius: 1,
              boxShadow: 3
            }}
          >
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ color: 'white', p: 1 }}
            >
              <MenuIcon fontSize="medium" />
            </IconButton>
          </Box>
        )}

        {selectedGame ? (
          <>
            {/* Video Player */}
            <Box sx={{
              flex: 1,
              position: 'relative',
              bgcolor: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Mode mobile portrait: hauteur adaptée
              ...(isMobile && {
                minHeight: '40vh',
                maxHeight: '60vh'
              }),
              // Mode paysage mobile: maximiser l'espace vidéo
              '@media (max-width: 900px) and (orientation: landscape)': {
                minHeight: 'calc(100vh - 100px)',
                maxHeight: 'calc(100vh - 100px)'
              }
            }}>
              <Box
                ref={playerWrapperRef}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {selectedGame.game.url ? (
                  <ReactPlayer
                    ref={playerRef}
                    url={selectedGame.game.url}
                    controls
                    playing={playing}
                    playbackRate={playbackRate}
                    width="100%"
                    height="100%"
                    onReady={() => {
                      if (playerWrapperRef.current) {
                        const rect = playerWrapperRef.current.getBoundingClientRect();
                        setPlayerSize({ width: rect.width, height: rect.height });
                      }
                    }}
                    onProgress={handleProgress}
                  />
                ) : (
                  <Typography variant="body1" color="error" sx={{ p: 2, color: 'white' }}>
                    {t('canteraStats.noVideoUrl')}
                  </Typography>
                )}

                {/* Overlay Canvas */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: playerSize.width,
                    height: playerSize.height,
                    pointerEvents: drawingEnabled ? 'auto' : 'none',
                    overflow: 'hidden'
                  }}
                >
                  <DrawingCanvas
                    width={playerSize.width}
                    height={playerSize.height}
                    isDrawingEnabled={drawingEnabled}
                    tool={drawTool}
                    color={drawColor}
                  />
                </Box>
              </Box>
            </Box>

            {/* Barre de contrôles en bas */}
            <Box
              sx={{
                borderTop: '1px solid #ddd',
                bgcolor: 'white',
                p: isMobile ? 1 : 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 1 : 1.5,
                // Mode paysage: contrôles ultra-compacts
                '@media (max-width: 900px) and (orientation: landscape)': {
                  p: 0.5,
                  gap: 0.5,
                  maxHeight: '80px',
                  overflowY: 'auto'
                }
              }}
            >
              {/* Contrôles vidéo */}
              <Box sx={{
                display: 'flex',
                gap: 0.5,
                overflowX: isMobile ? 'auto' : 'visible',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                alignItems: 'center',
                pb: isMobile ? 0.5 : 0,
                '&::-webkit-scrollbar': {
                  height: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px'
                }
              }}>
                <Button variant="contained" size="small" onClick={handlePlay} sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                  {isMobile ? '▶' : `▶ ${t('canteraStats.play')}`}
                </Button>
                <Button variant="outlined" size="small" onClick={handlePause} sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                  {isMobile ? '⏸' : `⏸ ${t('canteraStats.pause')}`}
                </Button>
                <Button variant="outlined" size="small" onClick={handleSlow} sx={{ minWidth: isMobile ? '50px' : 'auto' }}>0.5x</Button>
                <Button variant="outlined" size="small" onClick={handleNormalSpeed} sx={{ minWidth: isMobile ? '40px' : 'auto' }}>1x</Button>
                <Button variant="outlined" size="small" onClick={() => skipTime(-5)} sx={{ minWidth: isMobile ? '50px' : 'auto' }}>-5s</Button>
                <Button variant="outlined" size="small" onClick={() => skipTime(5)} sx={{ minWidth: isMobile ? '50px' : 'auto' }}>+5s</Button>
              </Box>

              {/* Navigation actions et outils de dessin */}
              <Box sx={{
                display: 'flex',
                gap: isMobile ? 1 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: 'wrap',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between'
              }}>
                {/* Navigation */}
                {selectedActionType && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: isMobile ? 'nowrap' : 'wrap',
                    overflowX: isMobile ? 'auto' : 'visible',
                    pb: isMobile ? 0.5 : 0,
                    '&::-webkit-scrollbar': {
                      height: '4px'
                    }
                  }}>
                    <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', mr: 0.5 }}>
                      {isMobile ? selectedActionType.substring(0, 15) + (selectedActionType.length > 15 ? '...' : '') : selectedActionType}
                    </Typography>
                    <Button variant="contained" size="small" onClick={handlePrev} sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                      {isMobile ? '◀' : `◀ ${t('canteraStats.previous')}`}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleNext} sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                      {isMobile ? '▶' : `${t('canteraStats.next')} ▶`}
                    </Button>
                    <Typography variant="caption" sx={{ px: 0.5, whiteSpace: 'nowrap' }}>
                      {currentIndex + 1} / {getTimesForActionType(selectedActionType).length}
                    </Typography>
                  </Box>
                )}

                {/* Outils de dessin */}
                <Box sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: isMobile ? 'nowrap' : 'wrap',
                  overflowX: isMobile ? 'auto' : 'visible',
                  pb: isMobile ? 0.5 : 0,
                  '&::-webkit-scrollbar': {
                    height: '4px'
                  }
                }}>
                  <Button
                    variant={drawingEnabled ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setDrawingEnabled(!drawingEnabled)}
                    sx={{ minWidth: isMobile ? '70px' : 'auto' }}
                  >
                    {isMobile ? '✏️' : `✏️ ${drawingEnabled ? t('canteraStats.drawingOn') : t('canteraStats.drawingOff')}`}
                  </Button>
                  {drawingEnabled && (
                    <>
                      <Button
                        variant={drawTool === 'pen' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setDrawTool('pen')}
                        sx={{ minWidth: isMobile ? '50px' : 'auto' }}
                      >
                        {isMobile ? '✏' : t('canteraStats.pen')}
                      </Button>
                      <Button
                        variant={drawTool === 'eraser' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setDrawTool('eraser')}
                        sx={{ minWidth: isMobile ? '50px' : 'auto' }}
                      >
                        {isMobile ? '⌫' : t('canteraStats.eraser')}
                      </Button>
                      <Button
                        size="small"
                        sx={{ minWidth: '36px', bgcolor: '#ff0000', color: 'white', '&:hover': { bgcolor: '#cc0000' } }}
                        onClick={() => {
                          setDrawTool('pen');
                          setDrawColor('#ff0000');
                        }}
                      >
                        🔴
                      </Button>
                      <Button
                        size="small"
                        sx={{ minWidth: '36px', bgcolor: '#0000ff', color: 'white', '&:hover': { bgcolor: '#0000cc' } }}
                        onClick={() => {
                          setDrawTool('pen');
                          setDrawColor('#0000ff');
                        }}
                      >
                        🔵
                      </Button>
                      <Button
                        size="small"
                        sx={{ minWidth: '36px', bgcolor: '#00ff00', color: 'white', '&:hover': { bgcolor: '#00cc00' } }}
                        onClick={() => {
                          setDrawTool('pen');
                          setDrawColor('#00ff00');
                        }}
                      >
                        🟢
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearDrawing}
                        sx={{ minWidth: isMobile ? '50px' : 'auto' }}
                      >
                        {isMobile ? '🗑️' : `🗑️ ${t('canteraStats.clear')}`}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              p: 2
            }}
          >
            <Typography variant={isMobile ? 'body1' : 'h6'} color="text.secondary" align="center">
              {isMobile ? t('canteraStats.selectMatchMobile') : t('canteraStats.selectMatch')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
